<template>
  <BaseModal
    :isOpen="open"
    maxWidth="xl"
    aria-modal="true"
    role="dialog"
    @close="handleClose"
  >
    <form
      class="relative max-h-[calc(100vh-6rem)] overflow-y-auto"
      @submit.prevent="handleSubmit"
    >
      <header class="flex items-start justify-between">
        <div>
          <h2 class="text-2xl font-semibold text-primary-dark-text">
            {{ mode === "edit" ? "Edit Member" : "Add Member" }}
          </h2>
          <p class="mt-2 text-sm text-gray-500">
            {{
              mode === "edit"
                ? "Edit member details below."
                : "After adding, the member will be listed in the table."
            }}
          </p>
        </div>
      </header>

      <div class="mt-8 space-y-6 sm:mt-10">
        <div>
          <label
            class="text-sm font-semibold text-primary-dark-text"
            for="username"
            >Username</label
          >
          <input
            id="username"
            v-model="username"
            class="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-primary-dark-text placeholder:text-gray-400 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Enter your username"
            type="text"
          />
          <p
            v-if="submitted && usernameError"
            class="mt-2 text-sm text-red-500"
          >
            {{ usernameError }}
          </p>
        </div>

        <div>
          <label
            class="text-sm font-semibold text-primary-dark-text"
            for="first-name"
            >First Name</label
          >
          <input
            id="first-name"
            v-model="firstName"
            class="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-primary-dark-text placeholder:text-gray-400 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Enter your First Name"
            type="text"
          />
          <p
            v-if="submitted && firstNameError"
            class="mt-2 text-sm text-red-500"
          >
            {{ firstNameError }}
          </p>
        </div>

        <div>
          <label
            class="text-sm font-semibold text-primary-dark-text"
            for="last-name"
            >Last Name</label
          >
          <input
            id="last-name"
            v-model="lastName"
            class="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-primary-dark-text placeholder:text-gray-400 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Enter your Last Name"
            type="text"
          />
          <p
            v-if="submitted && lastNameError"
            class="mt-2 text-sm text-red-500"
          >
            {{ lastNameError }}
          </p>
        </div>

        <div>
          <label
            class="text-sm font-semibold text-primary-dark-text"
            for="email"
            >Email address</label
          >
          <input
            id="email"
            v-model="email"
            class="mt-2 block w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-primary-dark-text placeholder:text-gray-400 shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Enter your email address"
            type="email"
          />
          <p v-if="submitted && emailError" class="mt-2 text-sm text-red-500">
            {{ emailError }}
          </p>
        </div>
      </div>

      <p v-if="serverError" class="mt-6 text-sm text-red-500">
        {{ serverError }}
      </p>

      <div
        class="mt-8 flex flex-col items-stretch justify-end gap-4 border-t border-gray-100 pt-6 sm:mt-10 sm:flex-row sm:items-center sm:gap-6"
      >
        <button
          class="text-sm font-semibold text-primary-dark-text transition hover:text-primary-neutral"
          type="button"
          @click="handleClose"
        >
          Cancel
        </button>
        <button
          class="rounded-lg bg-btn-gradient px-7 py-3 text-sm font-semibold text-white shadow-lg transition hover:opacity-90"
          type="submit"
        >
          Save
        </button>
      </div>
    </form>
  </BaseModal>
</template>

<script>
import { createTargetUser, updateTargetUser } from "../services/targetUsers";
import BaseModal from "@/components/ui/BaseModal.vue";

export default {
  components: {
    BaseModal,
  },
  data() {
    return {
      email: "",
      username: "",
      firstName: "",
      lastName: "",
      submitted: false,
      serverError: "",
    };
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  computed: {
    usernameError() {
      return this.username.length === 0 ? "Username is required" : null;
    },
    firstNameError() {
      return this.firstName.length === 0 ? "First name is required" : null;
    },
    lastNameError() {
      return this.lastName.length === 0 ? "Last name is required" : null;
    },
    emailError() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !emailPattern.test(this.email) ? "Email is invalid" : null;
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.email = newValue.email;
          this.username = newValue.username;
          this.firstName = newValue.firstName;
          this.lastName = newValue.lastName;
          this.submitted = false;
          this.serverError = "";
        } else {
          this.resetForm();
        }
      },
    },
  },
  methods: {
    async handleSubmit() {
      this.submitted = true;
      this.serverError = "";
      if (
        this.usernameError ||
        this.firstNameError ||
        this.lastNameError ||
        this.emailError
      ) {
        return;
      }

      const userData = {
        email: this.email,
        username: this.username,
        first_name: this.firstName,
        last_name: this.lastName,
        is_active: true,
      };

      try {
        if (this.mode === "edit") {
          await updateTargetUser(this.user.id, userData);
          this.$emit("userUpdated");
          this.$emit("alert", {
            type: "success",
            message: "User updated successfully.",
          });
        } else {
          await createTargetUser(userData);
          this.$emit("userAdded");
        }
        this.$emit("close");
        this.resetForm();
      } catch (error) {
        this.serverError =
          error.response?.data?.detail +
            " Please check if the user is in the inactive members list." ||
          "An error occurred. Please try again.";
      }
    },
    resetForm() {
      this.email = "";
      this.username = "";
      this.firstName = "";
      this.lastName = "";
      this.submitted = false;
      this.serverError = "";
    },
    handleClose() {
      this.$emit("close");
      this.resetForm();
    },
  },
};
</script>

<style scoped>
input,
.multiselect {
  width: 100%;
}

@import "vue-multiselect/dist/vue-multiselect.css";
@import url("../css/styles/vue-multiselect.css");
@import url("../css/styles/polls.css");

@media (max-width: 767px) {
  /* Adjustments for smaller screens */
  .btn-blue {
    background-color: #4271f4;
    color: #fff;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    margin-bottom: 8px;
  }
}
</style>
